import {
  InstrumentStatus,
  parsePaymentInstrument,
  type PaymentInstrument,
} from '@foundation/apis/payments';

import { InstrumentUrlApiResponse } from '@/services/base/schemas';
import { execGet, execPost } from '@/services/utils/RequestUtils';
import { isSepaMandatesEnabled } from '@/utils/env';

import { type IManagePaymentInstrument } from './MandateReference';
import {
  MandateDetailsApiResponse,
  ManagePaymentInstrumentApiResponse,
  PaymentInstrumentApiResponse,
} from './PaymentInstrumentsApiResponse';
import { TokenizationPayload } from './TokenizationPayload';
import { withMandateReferenceAsId } from '../utils/InstrumentUtils';

export async function getPaymentIntentInstruments(
  paymentIntentId: string,
  accessToken: string,
): Promise<ReadonlyArray<PaymentInstrument>> {
  const response = await execGet<PaymentInstrumentApiResponse[]>(
    `paymentintent/${paymentIntentId}/paymentdata`,
    accessToken,
  );

  return response
    .map(parsePaymentInstrumentResponse)
    .filter(
      (instrument): instrument is PaymentInstrument => instrument !== undefined,
    );
}

export function parsePaymentInstrumentResponse(
  response: PaymentInstrumentApiResponse,
): PaymentInstrument | undefined {
  const instrument = parsePaymentInstrument(response);
  return instrument && withMandateReferenceAsId(instrument);
}

export async function getManagePaymentInstruments(
  customerNumber: string,
  accessToken: string,
  legalEntityNumber?: number,
): Promise<ReadonlyArray<IManagePaymentInstrument>> {
  const query = new URLSearchParams({ customerNumber });
  if (isSepaMandatesEnabled()) {
    query.append('includePendingMandates', 'true');
  }
  if (legalEntityNumber !== undefined) {
    query.append('legalEntityNumber', legalEntityNumber.toString());
  }

  const response = await execGet<ManagePaymentInstrumentApiResponse[]>(
    `paymentinstruments?${query}`,
    accessToken,
  );

  const instruments = response.flatMap((raw) => {
    const instrument = parsePaymentInstrument(raw);
    const isInactiveMandate =
      raw.mandateReference !== undefined &&
      raw.status !== 'Pending' &&
      raw.status !== 'Active';
    return instrument && !isInactiveMandate
      ? [{ ...withMandateReferenceAsId(instrument), mandateStatus: raw.status }]
      : [];
  });

  return sortAndFlagDuplicates(instruments);
}

// Prioritizes pending mandates, then mirrors the ordering and duplicate flagging of getPaymentInstruments in @foundation/apis/payments.
function sortAndFlagDuplicates(
  instruments: ReadonlyArray<IManagePaymentInstrument>,
): ReadonlyArray<IManagePaymentInstrument> {
  const isExpired = (instrument: IManagePaymentInstrument) =>
    instrument.status === InstrumentStatus.Expired;

  const isPending = (instrument: IManagePaymentInstrument) =>
    instrument.mandateStatus === 'Pending';

  const sorted = [...instruments].sort(
    (a, b) =>
      Number(isPending(b)) - Number(isPending(a)) ||
      Number(isExpired(a)) - Number(isExpired(b)) ||
      Number(b.isDefault) - Number(a.isDefault),
  );

  return sorted.map((instrument) =>
    sorted.filter((other) => other.maskedInfo === instrument.maskedInfo)
      .length > 1
      ? { ...instrument, isDuplicate: true }
      : instrument,
  );
}

export async function revokeMandate(
  mandateReference: string,
  accessToken: string,
): Promise<void> {
  await execPost(
    `paymentinstruments/mandates/${encodeURIComponent(
      mandateReference,
    )}/revoke`,
    {},
    accessToken,
  );
}

export interface CreateSepaMandatePayload {
  readonly legalEntityNumber: number;
  readonly accountHolderName: string;
  readonly iban: string;
  readonly consentAccepted: boolean;
  readonly customerCulture: string;
}

export interface CreateSepaMandateResponse {
  readonly mandateReference: string;
  readonly mandateStatus: string;
}

export function createSepaMandate(
  payload: CreateSepaMandatePayload,
  accessToken: string,
): Promise<CreateSepaMandateResponse> {
  return execPost<CreateSepaMandateResponse>(
    'paymentinstruments/mandates/sepa',
    payload,
    accessToken,
  );
}

export function getMandateByReference(
  mandateReference: string,
  accessToken: string,
): Promise<MandateDetailsApiResponse> {
  return execGet<MandateDetailsApiResponse>(
    `mandates/${encodeURIComponent(mandateReference)}?loadMandators=true`,
    accessToken,
  );
}

export async function activateMandate(
  mandateReference: string,
  accessToken: string,
): Promise<void> {
  await execPost(
    `mandates/by-reference/${encodeURIComponent(mandateReference)}/activate`,
    {},
    accessToken,
  );
}

export async function paymentInstrumentTokenize(
  payload: TokenizationPayload,
  accessToken: string,
): Promise<string> {
  const response = await execPost<InstrumentUrlApiResponse>(
    `paymentinstruments`,
    {
      legalEntityNumber: payload.legalEntityNumber,
      customerNumber: payload.customerNumber,
      paymentDataTypeId: payload.paymentModuleId,
      braintreeInstrument: payload.braintreeInstrument,
    },
    accessToken,
  );

  return response.redirectUrl;
}
